// Cause & Effect API client.
//
// The automation endpoints (SPEC_AUTOMATION_CAUSE_EFFECT.md §9) are not deployed yet. Rather
// than let every screen throw, each call funnels through `call()`, which turns a missing backend
// into an explicit `unavailable: true` result. The UI reads that flag and says so plainly —
// it never fakes rules, and it never presents an empty list as "you have no rules configured"
// when the truth is "this API isn't live yet".
//
// Everything the builder needs to COMPOSE a rule (sites, assets, gateways, IO mappings) comes
// from endpoints that already exist in production, so the builder is fully usable against real
// devices before any of this ships. Only persistence waits on the backend.

import { api } from "./api";
import { OUTPUT_SIGNAL_TYPES, sourceKey } from "./automationModel";

const BASE = "/automation";

// A 404 on the collection route means the router isn't mounted; 501 is the explicit
// "implemented but deliberately switched off" case. Anything else is a real error worth
// surfacing to the operator.
function isNotDeployed(err) {
  const s = err?.response?.status;
  return s === 404 || s === 501;
}

async function call(fn) {
  try {
    const r = await fn();
    return { ok: true, data: r.data, unavailable: false, error: null };
  } catch (err) {
    if (isNotDeployed(err)) {
      return { ok: false, data: null, unavailable: true, error: null };
    }
    return {
      ok: false,
      data: null,
      unavailable: false,
      error: err?.response?.data?.detail || err?.message || "Request failed",
    };
  }
}

export const automationApi = {
  listRules: (params) => call(() => api.get(`${BASE}/rules`, { params })),
  getRule: (ruleId) => call(() => api.get(`${BASE}/rules/${ruleId}`)),
  createRule: (body) => call(() => api.post(`${BASE}/rules`, body)),
  updateRule: (ruleId, body) => call(() => api.patch(`${BASE}/rules/${ruleId}`, body)),
  deleteRule: (ruleId) => call(() => api.delete(`${BASE}/rules/${ruleId}`)),
  setMode: (ruleId, mode) => call(() => api.post(`${BASE}/rules/${ruleId}/mode`, { mode })),
  resetRule: (ruleId) => call(() => api.post(`${BASE}/rules/${ruleId}/reset`, {})),
  testRule: (ruleId) => call(() => api.post(`${BASE}/rules/${ruleId}/test`, {})),
  listRuns: (ruleId, params) => call(() => api.get(`${BASE}/rules/${ruleId}/runs`, { params })),
  matrix: (siteId) => call(() => api.get(`${BASE}/matrix`, { params: { site_id: siteId } })),
  setOverride: (body) => call(() => api.post(`${BASE}/overrides`, body)),
  listOverrides: (gatewayId) => call(() => api.get(`${BASE}/overrides`, { params: { gateway_id: gatewayId } })),
};

// ── Device catalogue — all from endpoints that exist today ────────────────────

// Assets at a site, in the shape the builder's dropdowns want.
export async function loadAssets(siteId) {
  const r = await call(() => api.get("/assets", { params: { site_id: siteId } }));
  return r.ok ? r.data || [] : [];
}

// Every IO point across the site's gateways, split into readable inputs and writable outputs.
// One request per gateway — a site normally has one, occasionally a handful.
export async function loadIoPoints(siteId) {
  const gwRes = await call(() => api.get("/hardware/gateways"));
  const gateways = (gwRes.ok ? gwRes.data || [] : []).filter((g) => g.site_id === siteId);

  const perGateway = await Promise.all(
    gateways.map(async (gw) => {
      const r = await call(() => api.get(`/hardware/gateways/${gw.gateway_id}/io-mappings`));
      const mappings = r.ok ? r.data || [] : [];
      return mappings.map((m) => {
        // A write targets io_point_address (the gateway's own tag or the direct-addressed coil);
        // a read watches io_point_name (the key the register arrives under in telemetry). They
        // differ on the WT-460M — OUT1 writes, output-1 reads back — which is exactly why the
        // readback field exists. Getting these two backwards drives the wrong register.
        const writeTag = m.io_point_address || m.io_point_name;
        return {
          gateway_id: gw.gateway_id,
          gateway_name: gw.gateway_name || gw.gateway_id,
          io_point_name: m.io_point_name,
          write_tag: writeTag,
          readback: m.readback_io_point_name || writeTag,
          device_id: m.device_id || null,
          label: m.io_point_name || writeTag,
          description: m.description || "",
          signal_type: m.signal_type || "",
          unit: m.engineering_unit || "",
          writable: OUTPUT_SIGNAL_TYPES.includes(m.signal_type),
          addressing:
            m.write_mode === "direct"
              ? `slave ${m.modbus_slave_id} · coil ${m.modbus_start_address} · FC${m.modbus_function_code ?? 15}`
              : null,
        };
      });
    })
  );

  return { gateways, ioPoints: perGateway.flat() };
}

// Index IO points by the same key `sourceKey()` produces, so validation and rendering can look
// one up from a rule's stored source without re-deriving anything.
export function indexIoPoints(ioPoints) {
  const out = {};
  for (const p of ioPoints) {
    out[sourceKey({ kind: "io", gateway_id: p.gateway_id, io_point_name: p.io_point_name })] = p;
  }
  return out;
}

export function indexAssets(assets) {
  const out = {};
  for (const a of assets) out[a.asset_id] = a;
  return out;
}

// Live values for every linked asset at the site — lets the builder show what a device reads
// right now while a threshold is being typed, which is the difference between "50" being a
// guess and "50" being a decision.
export async function loadLiveValues(siteId) {
  const r = await call(() => api.get("/assets/live-values", { params: { site_id: siteId } }));
  return r.ok ? r.data || {} : {};
}
